import * as XLSX from 'xlsx';
import { GRADE_LEVELS } from '../../../shared/constant/gradeLevels';
import { getStrandsForGradeLevel } from '../../../shared/constant/strands';

// Reads a DepEd School Form 1 (School Register) export, one file per section.
//
// The header details (grade level, section, school year, track) are first read from fixed positions
// in the official SF1-SHS template (sheet "school_form_1_shs_ver2018.2.1.1"), relative to the LRN
// header row, since their labels sit in merged cells that don't share a row with the value. If that
// finds nothing (another template version, or the junior high form), the label is searched for
// instead. Either way the review step lets staff correct them before importing.
const FIXED_POSITIONS = {
  gradeLevel: { row: -9, col: 29 },
  schoolYear: { row: -9, col: 18 },
  track: { row: -7, col: 42 },
  section: { row: -2, col: 8 },
  course: { row: -2, col: 19 },
};
const HEADER_TO_DATA_START_ROW_OFFSET = 2;

const LABELS = {
  gradeLevel: /^grade\s*level$/i,
  schoolYear: /^school\s*year$/i,
  track: /^track\s*(and|&)\s*strand$/i,
  section: /^section$/i,
  course: /^course/i,
};
const ANY_LABEL = /^(school\s*(id|name|year)|region|division|district|grade\s*level|section|semester|track|course)/i;

// Below the student rows, SF1 has "<=== TOTAL MALE", "TOTAL FEMALE", "COMBINED" tallies and a
// remarks-code legend — not students.
const FOOTER_ROW_PATTERN = /^(<=+\s*)?(total\s+(male|female)|combined|code)\b/i;

function cell(rows, r, c) {
  return String(rows[r]?.[c] ?? '').trim();
}

// The first non-label value to the right of a label in the rows above the student list.
function findByLabel(rows, headerRow, pattern) {
  for (let r = 0; r < headerRow; r++) {
    const row = rows[r] || [];
    const labelCol = row.findIndex((value) => pattern.test(String(value ?? '').trim()));
    if (labelCol === -1) continue;
    for (let c = labelCol + 1; c < row.length; c++) {
      const value = cell(rows, r, c);
      if (!value) continue;
      if (ANY_LABEL.test(value)) break;
      return value;
    }
  }
  return '';
}

function readHeaderField(rows, headerRow, key) {
  const { row, col } = FIXED_POSITIONS[key];
  const fixed = cell(rows, headerRow + row, col);
  // In another layout the fixed spot can land on a label instead of a value.
  return (fixed && !ANY_LABEL.test(fixed) ? fixed : '') || findByLabel(rows, headerRow, LABELS[key]);
}

function findHeaderRow(rows) {
  return rows.findIndex((row) => String(row?.[0] ?? '').trim().toUpperCase() === 'LRN');
}

function findHeaderCol(rows, headerRow, pattern) {
  return (rows[headerRow] || []).findIndex((value) => pattern.test(String(value ?? '').trim()));
}

function normalizeGradeLevel(value) {
  const match = String(value ?? '').match(/\d+/);
  const grade = match ? match[0] : '';
  return GRADE_LEVELS.some((g) => g.value === grade) ? grade : '';
}

function normalizeSchoolYear(value) {
  const match = String(value ?? '').match(/(\d{4}).*?(\d{4})/);
  return match ? `${match[1]}-${match[2]}` : '';
}

// Matches the file's "Track and Strand" / "Course" text to one of our strand codes for that grade,
// by its code (e.g. "ICT") or its full name. '' when nothing matches; staff pick it in the review.
function guessStrand(gradeLevel, ...texts) {
  const haystack = texts.join(' ').toUpperCase();
  if (!haystack.trim()) return '';
  const match = getStrandsForGradeLevel(gradeLevel).find(({ code, label }) => {
    const name = label.replace(/\s*\(.*\)\s*$/, '').toUpperCase();
    return new RegExp(`\\b${code}\\b`).test(haystack) || haystack.includes(name);
  });
  return match?.code ?? '';
}

// SF1 combines "Last Name, First Name Name-Extension Middle Name" in one cell. Extensions (Jr., III)
// can't be told apart from a middle name reliably, so this is best-effort; staff can fix a name
// afterwards in the edit form.
function splitName(raw) {
  const [lastPart, ...rest] = String(raw ?? '').split(',');
  const lastname = (lastPart ?? '').trim();
  const restTokens = rest.join(' ').trim().split(/\s+/).filter(Boolean);
  return {
    lastname,
    firstname: restTokens[0] ?? '',
    middlename: restTokens.slice(1).join(' ') || null,
  };
}

// Resolves to { details: { grade_level, section, school_year, strand }, students: [...] }. Each
// student has `error` set when the row can't be imported as it is.
export async function parseSf1File(file) {
  const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array' });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: '' });

  const headerRow = findHeaderRow(rows);
  if (headerRow === -1) {
    throw new Error("This doesn't look like a DepEd SF1 file (couldn't find the LRN column).");
  }

  const lrnCol = findHeaderCol(rows, headerRow, /^LRN$/i);
  const nameCol = findHeaderCol(rows, headerRow, /^NAME/i);
  const sexCol = findHeaderCol(rows, headerRow, /^SEX/i);
  const ageCol = findHeaderCol(rows, headerRow, /^AGE/i);
  if (nameCol === -1) {
    throw new Error("Couldn't find the NAME column in this SF1 file.");
  }

  const gradeLevel = normalizeGradeLevel(readHeaderField(rows, headerRow, 'gradeLevel'));
  const details = {
    grade_level: gradeLevel,
    section: readHeaderField(rows, headerRow, 'section'),
    school_year: normalizeSchoolYear(readHeaderField(rows, headerRow, 'schoolYear')),
    strand: guessStrand(
      gradeLevel,
      readHeaderField(rows, headerRow, 'track'),
      readHeaderField(rows, headerRow, 'course')
    ),
  };

  const students = [];
  const seenLrns = new Set();
  for (let r = headerRow + HEADER_TO_DATA_START_ROW_OFFSET; r < rows.length; r++) {
    const lrnRaw = cell(rows, r, lrnCol);
    const nameRaw = cell(rows, r, nameCol);
    if (!lrnRaw && !nameRaw) break; // end of the student list
    // A tally row sits between the male and female lists, so keep going past it.
    if (FOOTER_ROW_PATTERN.test(nameRaw) || FOOTER_ROW_PATTERN.test(lrnRaw)) continue;

    const lrn = lrnRaw.replace(/\D/g, '');
    const { lastname, firstname, middlename } = splitName(nameRaw);
    if (!lrn && !firstname && !lastname) continue;

    const sex = sexCol === -1 ? '' : cell(rows, r, sexCol).toUpperCase().charAt(0);
    const ageRaw = ageCol === -1 ? '' : cell(rows, r, ageCol).replace(/\D/g, '');

    const errors = [];
    if (!/^\d{12}$/.test(lrn)) errors.push('LRN must be 12 digits');
    else if (seenLrns.has(lrn)) errors.push('Listed twice in this file');
    if (!firstname || !lastname) errors.push('Name must be "Last Name, First Name"');
    seenLrns.add(lrn);

    students.push({
      lrn,
      firstname,
      lastname,
      middlename,
      gender: sex === 'M' || sex === 'F' ? sex : null,
      age: ageRaw ? Number(ageRaw) : null,
      error: errors.join(', ') || null,
    });
  }

  if (students.length === 0) {
    throw new Error('No students were found under the LRN header in this file.');
  }
  return { details, students };
}
